/**
 * Validation helper for the photo_upload question type.
 *
 * Used by the SurveySession to decide whether the respondent can advance to
 * the next question. The respondent is allowed to proceed when:
 *   - At least one image has finished UPLOADING (`extraction_status` is
 *     'completed', 'processing' or 'failed' — the image is already in S3; the
 *     product extraction that follows is researcher-side enrichment, not a gate).
 *     Previously this required `=== 'completed'`, so a valid photo whose extraction
 *     returned zero products (status 'failed') left "Next" disabled. OR
 *   - The respondent provided a non-empty typed answer via the
 *     ``text_fallback`` field (the fail-safe still offered when parsing fails).
 *
 * ...AND no image is still in flight. That second condition was missing: the check used `.some()`,
 * so one finished upload enabled Next while another was mid-flight. Tapping Next then persisted the
 * placeholder — `{ url: "", s3_key: "uploading-<ts>-0", extraction_status: "uploading" }` — as part
 * of the answer (DEV session 5234). The file itself reached S3 three seconds later, so nothing was
 * lost server-side, but the stored answer points at a key that never exists, making the photo
 * invisible to the results page, the XLSX export and the extraction pipeline.
 */

export interface PhotoUploadResponseLike {
  images?: Array<{ extraction_status?: string }>;
  text_fallback?: string;
  [key: string]: unknown;
}

// extraction_status values that mean the image reached S3 (vs. an in-flight
// 'uploading'/'pending' placeholder). Product extraction (completed vs failed) is
// researcher-side enrichment, not a gate on whether the respondent has answered.
const UPLOADED_STATUSES = new Set(['completed', 'processing', 'failed']);

/** True while this image is only a local placeholder — no S3 object exists for it yet. */
export const isImageInFlight = (
  image: { extraction_status?: string } | null | undefined,
): boolean => !UPLOADED_STATUSES.has(image?.extraction_status ?? '');

/** True when any image would be persisted as an unresolvable placeholder. */
export const hasInFlightUpload = (response: PhotoUploadResponseLike | null | undefined): boolean =>
  Array.isArray(response?.images) && response.images.some(isImageInFlight);

export const isPhotoUploadResponseValid = (
  response: PhotoUploadResponseLike | null | undefined,
): boolean => {
  if (!response) return false;

  // Checked before either disjunct: an in-flight upload must block advancing even when a text
  // fallback was also typed, otherwise the answer keeps a placeholder image alongside real text.
  if (hasInFlightUpload(response)) return false;

  const hasUploadedImage =
    Array.isArray(response.images) &&
    response.images.some((img) => UPLOADED_STATUSES.has(img?.extraction_status ?? ''));

  const hasTextFallback =
    typeof response.text_fallback === 'string' && response.text_fallback.trim().length > 0;

  return hasUploadedImage || hasTextFallback;
};
